import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PayPalCheckout from "@/components/PayPalCheckout";
import { resolveAssetUrl } from "@/lib/assetUrl";

// Conversion: 100 TBC Points = $1
const POINTS_PER_DOLLAR = 100;
// Stripe minimum charge buffer — keep at least $0.50 chargeable after discount.
const MIN_CHARGE_USD = 0.5;

export default function CartPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [applyPoints, setApplyPoints] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCart();
      fetchPoints();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(response.data.cart || { items: [], total: 0 });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPoints = async () => {
    try {
      const r = await axios.get(`${API}/me/points`, { withCredentials: true });
      setPointsBalance(Number(r.data?.balance || 0));
    } catch (e) {
      // Non-fatal — user might not have points yet
      setPointsBalance(0);
    }
  };

  // Compute the redemption preview locally so the UI is responsive. Backend re-validates.
  const redemption = useMemo(() => {
    const subtotal = Number(cart.total || 0);
    if (!applyPoints || pointsBalance <= 0 || subtotal <= 0) {
      return { points: 0, discount: 0, finalTotal: subtotal };
    }
    // Max dollars we can discount = subtotal - min charge.
    const maxDiscount = Math.max(0, +(subtotal - MIN_CHARGE_USD).toFixed(2));
    const balanceDiscount = pointsBalance / POINTS_PER_DOLLAR;
    const discountDollars = Math.min(balanceDiscount, maxDiscount);
    const points = Math.floor(discountDollars * POINTS_PER_DOLLAR);
    const discount = +(points / POINTS_PER_DOLLAR).toFixed(2);
    const finalTotal = +(subtotal - discount).toFixed(2);
    return { points, discount, finalTotal };
  }, [applyPoints, pointsBalance, cart.total]);

  const updateQuantity = async (productId, quantity, variantKey) => {
    try {
      await axios.put(
        `${API}/cart/update`,
        { product_id: productId, quantity, variant_key: variantKey || null },
        { withCredentials: true }
      );
      fetchCart();
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const removeItem = async (productId, variantKey) => {
    try {
      const url = variantKey
        ? `${API}/cart/remove/${productId}?variant_key=${encodeURIComponent(variantKey)}`
        : `${API}/cart/remove/${productId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success("Item removed");
      fetchCart();
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, { withCredentials: true });
      toast.success("Cart cleared");
      fetchCart();
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (error) {
      toast.error("Failed to clear cart");
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      login();
      return;
    }
    // Multi-step Phase-4 checkout lives at /checkout — it collects pickup,
    // details, tip, then POSTs to /api/checkout and redirects to Stripe.
    // TBC Points redemption still runs through /checkout's body: forward
    // the current toggle state via router state for prefill.
    navigate("/checkout", {
      state: {
        applyPoints,
        pointsToRedeem: applyPoints ? redemption.points : 0,
      },
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-tbc-navy mb-4">Your Cart</h1>
          <p className="text-gray-600 font-body mb-8">Please sign in to view your cart</p>
          <Button
            onClick={login}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
            data-testid="login-to-cart"
          >
            Sign In
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const subtotal = Number(cart.total || 0);
  const pointsRedeemable = pointsBalance > 0 && subtotal > MIN_CHARGE_USD;
  const balanceDollarValue = +(pointsBalance / POINTS_PER_DOLLAR).toFixed(2);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-heading text-3xl text-tbc-navy">Your Cart</h1>
            {cart.items.length > 0 && (
              <Button
                variant="ghost"
                onClick={clearCart}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                data-testid="clear-cart"
              >
                Clear Cart
              </Button>
            )}
          </div>

          {cart.items.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="font-heading text-xl text-tbc-navy mb-2">Your cart is empty</h2>
              <p className="text-gray-600 font-body mb-6">
                Discover amazing products from Black-owned businesses
              </p>
              <Link to="/directory">
                <Button
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                  data-testid="continue-shopping"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cart Items */}
              <div className="space-y-4">
                {cart.items.map((item) => {
                  const variantKey = item.variant_key || "base";
                  const rowKey = `${item.product_id}::${variantKey}`;
                  const mods = item.modifier_labels || [];
                  const addons = item.addon_labels || [];
                  const si = item.special_instructions;
                  return (
                    <div
                      key={rowKey}
                      className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-tbc-cream">
                          <img
                            src={resolveAssetUrl(item.product?.image_url) || "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=200&q=80"}
                            alt={item.product?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </Link>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <Link to={`/product/${item.product_id}`}>
                              <h3 className="font-heading text-base sm:text-lg text-tbc-navy hover:text-tbc-gold leading-tight">
                                {item.product?.name}
                              </h3>
                            </Link>
                            <p className="text-gray-500 text-xs font-body mt-0.5">{item.business_name}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-heading text-sm sm:text-base text-tbc-navy">
                              ${Number(item.subtotal || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        {/* Modifier + add-on breakdown — matches the reference:
                            "Sides: Mac and Cheese, Collard Greens · Extra: Cornbread (+$2.50)" */}
                        {(mods.length > 0 || addons.length > 0 || si) && (
                          <div className="mt-2 space-y-0.5 text-xs sm:text-sm font-body text-gray-600" data-testid={`cart-item-config-${item.product_id}`}>
                            {mods.map((g) => (
                              <p key={g.group_id} className="line-clamp-2">
                                <span className="text-tbc-navy font-medium">{g.group_name}:</span>{" "}
                                {g.options.map((o) => o.name).join(", ")}
                              </p>
                            ))}
                            {addons.map((a) => (
                              <p key={a.id} className="line-clamp-1">
                                <span className="text-tbc-navy font-medium">Extra:</span>{" "}
                                {a.name} (+${Number(a.price || 0).toFixed(2)})
                              </p>
                            ))}
                            {si && (
                              <p className="line-clamp-2">
                                <span className="text-tbc-navy font-medium">Note:</span> {si}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="mt-3 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-0.5">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1, variantKey)}
                              disabled={item.quantity <= 1}
                              aria-label="Decrease quantity"
                              className="w-7 h-7 rounded-full flex items-center justify-center text-tbc-navy disabled:opacity-40 hover:bg-tbc-cream"
                              data-testid={`decrease-${item.product_id}`}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-7 text-center font-body text-sm">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1, variantKey)}
                              aria-label="Increase quantity"
                              className="w-7 h-7 rounded-full flex items-center justify-center text-tbc-navy hover:bg-tbc-cream"
                              data-testid={`increase-${item.product_id}`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            <Link
                              to={`/product/${item.product_id}`}
                              className="text-xs text-gray-500 hover:text-tbc-navy font-body px-2 py-1"
                              data-testid={`edit-${item.product_id}`}
                            >
                              Edit
                            </Link>
                            <button
                              type="button"
                              onClick={() => removeItem(item.product_id, variantKey)}
                              aria-label="Remove"
                              className="w-8 h-8 rounded-full flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`remove-${item.product_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* TBC Points Redemption */}
              {pointsBalance > 0 && (
                <div
                  className="rounded-xl p-5 border border-tbc-gold/40 bg-gradient-to-br from-tbc-navy to-[#0a1426] text-white shadow-sm"
                  data-testid="points-redeem-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-tbc-gold/20 border border-tbc-gold/50 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-tbc-gold" />
                      </div>
                      <div>
                        <p className="font-heading text-base text-white">
                          Use your TBC Points
                        </p>
                        <p className="text-sm text-white/70 font-body mt-0.5">
                          Balance: <span className="text-tbc-gold font-semibold">{pointsBalance.toLocaleString()} pts</span> <span className="text-white/40">·</span> worth ${balanceDollarValue.toFixed(2)}
                        </p>
                        {applyPoints && redemption.points > 0 && (
                          <p className="text-xs text-white/60 mt-1.5 font-body" data-testid="points-redeem-preview">
                            Applying <span className="text-tbc-gold">{redemption.points.toLocaleString()} pts</span> for <span className="text-tbc-gold">−${redemption.discount.toFixed(2)}</span> off
                          </p>
                        )}
                        {applyPoints && redemption.points === 0 && (
                          <p className="text-xs text-amber-300 mt-1.5 font-body">
                            Order total too small to apply points (minimum charge ${MIN_CHARGE_USD.toFixed(2)}).
                          </p>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={applyPoints}
                      onCheckedChange={setApplyPoints}
                      disabled={!pointsRedeemable}
                      className="data-[state=checked]:bg-tbc-gold"
                      data-testid="apply-points-toggle"
                    />
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-tbc-cream rounded-xl p-6 space-y-4">
                <div className="flex justify-between text-gray-600 font-body">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {applyPoints && redemption.discount > 0 && (
                  <div className="flex justify-between text-tbc-gold font-body" data-testid="points-discount-row">
                    <span>TBC Points ({redemption.points.toLocaleString()} pts)</span>
                    <span>−${redemption.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600 font-body">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="border-t border-tbc-gold/20 pt-4">
                  <div className="flex justify-between font-heading text-xl text-tbc-navy">
                    <span>Total</span>
                    <span data-testid="cart-total">${redemption.finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full py-6 text-lg font-medium"
                  data-testid="checkout-button"
                >
                  {checkingOut ? (
                    <span className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Proceed to Checkout
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </span>
                  )}
                </Button>

                {/* Alternate payment: PayPal. Silent no-op when PayPal env
                    keys aren't configured — appears once admin sets them. */}
                <PayPalCheckout disabled={checkingOut} />
              </div>

              <Link
                to="/directory"
                className="block text-center text-tbc-navy hover:text-tbc-gold font-body transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
